import {
  CLINIC_EDIT_FIELDS,
  CLINIC_JSON_FIELDS,
  CLINIC_NUMBER_FIELDS,
} from "./clinicFields";

const BOOLEAN_FIELDS = new Set([
  "unifi_scraping_enabled", "is_active", "outbound_calls_enabled", "transfer_campaigns_enabled",
]);
const isBooleanField = (f) => BOOLEAN_FIELDS.has(f);
const isNumberField = (f) => Object.prototype.hasOwnProperty.call(CLINIC_NUMBER_FIELDS, f);

// An integer, or null when the value is empty or not a number. Inputs hand
// back strings, so "3" counts; "" and "abc" do not.
const toInt = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = typeof v === "number" ? v : Number(String(v).trim());
  if (!Number.isFinite(n)) return null;
  return Math.trunc(n);
};

const toBool = (v) => {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") {
    const s = v.trim().toLowerCase();
    if (s === "true") return true;
    if (s === "false") return false;
  }
  return !!v;
};

export function pickEditableClinic(clinic) {
  const out = {};

  for (const f of CLINIC_EDIT_FIELDS) {
    const v = clinic?.[f];

    if (CLINIC_JSON_FIELDS.has(f)) {
      out[f] = v === null || v === undefined ? null : v;
      continue;
    }

    if (isBooleanField(f)) {
      // is_active defaults to true when missing (e.g. pre-migration rows)
      if (f === "is_active" && (v === null || v === undefined)) {
        out[f] = true;
      } else {
        out[f] = toBool(v);
      }
      continue;
    }

    if (isNumberField(f)) {
      out[f] = toInt(v) ?? CLINIC_NUMBER_FIELDS[f];
      continue;
    }

    out[f] = v === null || v === undefined ? "" : String(v);
  }

  // ✅ NEW: keep a copy of clinic_id if you want it easy in the UI
  out.id = clinic?.id;

  // ✅ OPTIONAL: keep legacy agent_questions available in draft for other screens,
  // but NOT editable and NOT saved via PATCH
  out.legacy_agent_questions = clinic?.agent_questions ?? null;

  return out;
}

export function diffClinicPayload(original, edited) {
  const payload = {};

  for (const f of CLINIC_EDIT_FIELDS) {
    if (isBooleanField(f)) {
      const a = toBool(original?.[f]);
      const b = toBool(edited?.[f]);
      if (a !== b) payload[f] = b;
      continue;
    }

    if (CLINIC_JSON_FIELDS.has(f)) {
      const a = original?.[f];
      const b = edited?.[f];
      if (JSON.stringify(a) !== JSON.stringify(b)) payload[f] = b;
      continue;
    }

    if (isNumberField(f)) {
      const b = toInt(edited?.[f]);
      // Empty or unparseable: keep what the server has rather than send null.
      if (b === null) continue;
      const a = toInt(original?.[f]) ?? CLINIC_NUMBER_FIELDS[f];
      if (a !== b) payload[f] = b;
      continue;
    }

    const a = (original?.[f] ?? "").toString();
    const b = (edited?.[f] ?? "").toString();
    if (a !== b) payload[f] = edited[f] === "" ? null : edited[f];
  }

  if ("unifi_scraping_enabled" in payload && payload.unifi_scraping_enabled === false) {
    payload.unifi_console_id = null;
  }

  return payload;
}
